<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, ButtonGroup, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@sveltestrap/sveltestrap';

  import { Clipboard } from '@client/components/clipboard';
  import { CodeMirror } from '@client/components/codemirror';
  import { Icon } from '@client/components/icon';
  import { MergeView } from '@client/components/merge-view';
  import { Spinner } from '@client/components/spinner';

  import { SignaturesService } from '@client/services/signatures';

  import { appStore } from '@client/stores/app';

  import { debounce } from '@common/utils/debounce';
  import { convertHTMLEntities, generateUUID } from '@common/utils/strings';

  export let data = {};
  export let source = '';
  export let fields = [];
  export let selectedUser = {};
  export let recipientEmail = '';
  export let editLabel = 'label.EDIT_HTML';
  export let textOnly = false;
  export let showSource = false;
  export let pinned = false;
  export let showUnparsedPreview = false;
  export let actions = [];
  export let originalSource = '';

  let showDiff = false;
  let updatedSource = source;
  let sourceCopy = source;

  let previousEmail = recipientEmail;
  let previousSelectedUser = selectedUser.value;
  let previousFields = JSON.stringify(fields);

  let iframeSource = '';
  let isUpdating = false;
  let previewRequest = 0;

  let errors = false;
  let htmlLintErrors = [];
  let liquidLintErrors = [];

  let showBadges = true;
  let findReplaceShowing = false;

  let collapseUnchanged = true;
  let revertControls = false;

  let darkMode = false;
  let darkModeStyle = '';
  let copySuccess = false;
  let copyTimeout;

  const uuid = generateUUID();

  const toggleSource = () => {
    showSource = !showSource;
  };

  const onIframeLoad = function () {
    const body = this.contentWindow.document.body;
    const html = this.contentWindow.document.documentElement;

    this.style.visibility = 'visible';
    this.style.height = '0px';
    this.style.height =
      Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight) + 'px';
  };

  const updateSignaturePreview = async (refresh) => {
    if (!source) {
      // Clear rather than just bail, so an empty slot renders nothing instead of
      // whatever was there before. Emptying an open editor does not reach here --
      // the `source.length` guard on onSourceKeyUp short-circuits first.
      iframeSource = '';
      return '';
    }

    if (showUnparsedPreview) {
      iframeSource = source || '';
      return iframeSource;
    }

    isUpdating = true;

    const requestId = ++previewRequest;
    const template = textOnly ? source?.replace(/\n/g, '<br />') : source || '';

    // A failed render of an in-progress template is an expected outcome shown inline
    // below, not an app fault — skip error capture; breadcrumbs still record it.
    const response = await SignaturesService.getSignature(
      {
        username: selectedUser.value,
        scratchblockname: data._id ? data.rulename : undefined,
        scratchtemplate: template,
        scratchparams: fields,
        testData: {
          testsender: selectedUser.value,
          testrecipient: recipientEmail
        },
        params: fields,
        prependLines: 0
      },
      { captureErrors: false }
    );

    // Superseded by a newer request: keep what is on screen rather than rendering a stale response.
    if (requestId !== previewRequest) {
      return iframeSource;
    }

    isUpdating = false;

    if (response.status === 500) {
      return htmlErrors({ detail: [{ line: 1, message: response.message.split(',').shift() }] });
    }

    if (response?.actions) {
      actions = [...response.actions];
    }

    const { rendered } = response;

    if (refresh === true) {
      return rendered;
    }

    iframeSource = rendered;
  };

  const onSourceKeyUp = debounce(async () => {
    iframeSource = await updateSignaturePreview(true);
  }, 500);

  const onRevertChanges = () => {
    sourceCopy = originalSource;
    updatedSource = originalSource;
  };

  const onReviewClose = () => {
    showDiff = false;
    revertControls = false;
  };

  const onReviewCancel = () => {
    revertControls = false;
    sourceCopy = source;
    updatedSource = source;
  };

  const onModifiedChange = (newValue) => {
    updatedSource = newValue;
  };

  const onReviewApply = async () => {
    source = updatedSource;
    sourceCopy = source;
    showDiff = false;
    revertControls = false;
    iframeSource = await updateSignaturePreview(true);
  };

  const enableChoosingMode = () => {
    revertControls = true;
  };

  const checkForTopPanel = () => {
    const topPanel = document.querySelector('.cm-panels-top');
    findReplaceShowing = !!topPanel;
  };

  onMount(async () => {
    iframeSource = await updateSignaturePreview(true);
  });

  $: if (previousEmail !== recipientEmail) {
    updateSignaturePreview();
    previousEmail = recipientEmail;
  }

  $: if (fields.length && JSON.stringify(fields) !== previousFields) {
    updateSignaturePreview();
    previousFields = JSON.stringify(fields);
  }

  $: if (Object.keys(selectedUser).length && selectedUser.value !== previousSelectedUser) {
    updateSignaturePreview();
    previousSelectedUser = selectedUser.value;
  }

  const formatLintErrors = (messages) =>
    messages
      .map(({ line, message }) => `<div><b>Line ${line || 'n/a'}:</b><div>${convertHTMLEntities(message)}</div></div>`)
      .join('');

  const htmlErrors = ({ detail }) => {
    if (detail.length) {
      htmlLintErrors = formatLintErrors(detail);
    } else {
      htmlLintErrors = [];
    }
  };

  const liquidErrors = ({ detail }) => {
    if (detail.length) {
      liquidLintErrors = formatLintErrors(detail);
    } else {
      liquidLintErrors = [];
    }
  };

  const onToggleDiff = () => {
    showDiff = !showDiff;

    if (showDiff) {
      sourceCopy = source;
      updatedSource = source;
    }
  };

  const togglePinning = () => {
    pinned = !pinned;
  };

  const toggleDarkMode = () => {
    darkMode = !darkMode;
    darkModeStyle = darkMode ? `<style>img { filter: invert(1) hue-rotate(180deg); }</style>` : '';
  };

  const toggleUnparsed = () => {
    showUnparsedPreview = !showUnparsedPreview;
  };

  const onCopySuccess = () => {
    clearTimeout(copyTimeout);
    copySuccess = true;
    copyTimeout = setTimeout(() => (copySuccess = false), 2000);
  };

  $: collapseIcon = showBadges ? 'chevron-right' : 'chevron-left';
  $: hideContent = showBadges ? '' : 'hide';
  $: badgeOffset = findReplaceShowing ? 'offset' : '';
  $: modified = JSON.stringify(originalSource) !== JSON.stringify(source);
  $: scrolling = $appStore.mobile ? 'auto' : 'no';
  $: errors = htmlLintErrors.length || liquidLintErrors.length;
  $: source.length && onSourceKeyUp();
  $: updateSignaturePreview(showUnparsedPreview);
</script>

<div class="editor" class:pinned={pinned && showSource}>
  <div class="preview" class:darkmode={darkMode}>
    <Spinner bind:show={isUpdating} backdrop />
    <ButtonGroup size="sm">
      <Button data-testid="show-source" bind:active={showSource} on:click={toggleSource}>{$format(editLabel)}</Button>
      {#if showSource}
        <Button id="tooltip-{uuid}-pinning" bind:active={pinned} on:click={togglePinning}>
          <Icon name={pinned ? 'pin' : 'pin-off'} />
          <Tooltip target="tooltip-{uuid}-pinning" placement="top">
            <b>{$format('label.PREVIEW_PINNING')}</b>
            <div>{$format('label.TOOLTIP.PREVIEW_PINNING')}</div>
          </Tooltip>
        </Button>
      {/if}
      {#if !textOnly}
        <Button class="raw-preview" id="tooltip-{uuid}-raw" bind:active={showUnparsedPreview} on:click={toggleUnparsed}>
          <Icon name="hash" />
          <Tooltip target="tooltip-{uuid}-raw" placement="top">
            <b>{$format('label.UNPARSED_PREVIEW')}</b>
            <div>{$format('label.TOOLTIP.UNPARSED_PREVIEW')}</div>
          </Tooltip>
        </Button>
      {/if}
      <Button class="dark-preview" id="tooltip-{uuid}-darkmode" bind:active={darkMode} on:click={toggleDarkMode}>
        <Icon name="moon" />
        <Tooltip target="tooltip-{uuid}-darkmode" placement="top">
          <b>{$format('label.DARK_MODE_PREVIEW')}</b>
          <div>{$format('label.TOOLTIP.DARK_MODE_PREVIEW')}</div>
        </Tooltip>
      </Button>
      <Clipboard text={iframeSource} on:copy={onCopySuccess} let:copy>
        <Button
          class="copy-signature"
          id="tooltip-{uuid}-copy"
          aria-label={$format('label.COPY_SIGNATURE')}
          on:click={copy}
          disabled={!iframeSource}
        >
          <Icon name={copySuccess ? 'check' : 'copy'} />
          <Tooltip target="tooltip-{uuid}-copy" placement="top">
            <b>{$format('label.COPY_SIGNATURE')}</b>
            <div>{$format('label.TOOLTIP.COPY_SIGNATURE')}</div>
          </Tooltip>
        </Button>
      </Clipboard>
    </ButtonGroup>

    <iframe
      tabindex="-1"
      style="width: 100%; visibility: hidden;"
      title="preview"
      {scrolling}
      on:load={onIframeLoad}
      srcdoc={`
        <html>
          <head>${darkModeStyle}</head>
          <body>
            ${textOnly ? `<span style="font-family: sans-serif">${iframeSource || ''}</span>` : darkModeStyle + (iframeSource || '')}
          </body>
        </html>
      `}
    />
  </div>

  {#if showSource}
    <div class="source">
      <div class="source-tour"></div>
      <div class="badges {hideContent} {badgeOffset}">
        <Icon
          id="collapse-icon"
          class="collapse-icon"
          name={collapseIcon}
          role="button"
          on:click={() => (showBadges = !showBadges)}
        />
        <Tooltip target="collapse-icon" placement="left">
          {showBadges ? $format('label.HIDE_TOOLBAR') : $format('label.SHOW_TOOLBAR')}
        </Tooltip>
        <div class="collapsible-content {hideContent}">
          <div class="validation" class:errors>
            {#if errors}
              <span id="tooltip-errors">
                <Icon name="alert-triangle" />
                {$format('label.ERRORS')}
              </span>
              <Tooltip target="tooltip-errors" placement="top">
                <div class="lint-errors">
                  {#if htmlLintErrors.length}
                    <div class="error">
                      <b>{$format('label.HTML_ERRORS')}</b>
                      <div>{@html htmlLintErrors}</div>
                    </div>
                  {/if}
                  {#if liquidLintErrors.length}
                    <div class="error">
                      <b>{$format('label.LIQUID_ERRORS')}</b>
                      <div>{@html liquidLintErrors}</div>
                    </div>
                  {/if}
                </div>
              </Tooltip>
            {:else}
              <Icon name="check" />
              {#if textOnly}
                {$format('label.TEXT').toUpperCase()}
              {:else}
                {$format('label.HTML').toUpperCase()}
              {/if}
            {/if}
          </div>

          {#if modified}
            <div class="actions">
              <Button on:click={onToggleDiff} size="sm" style="flex-shrink: 0;"
                >{$format('label.REVIEW_CHANGES')}</Button
              >
            </div>
          {/if}
        </div>
      </div>
      <div class="editors">
        <CodeMirror
          bind:value={source}
          lineWrapping={$appStore.mobile}
          bind:data
          on:htmlLintErrors={htmlErrors}
          on:liquidLintErrors={liquidErrors}
          disableFormatting
        />
      </div>
    </div>
  {/if}
</div>

<Modal id="signature-merge-view" isOpen={showDiff} fade={false} size="xl">
  <ModalHeader toggle={onToggleDiff}>
    {@html $format('label.SIGNATURE_EDITOR_MODAL_HEADER', { values: { rulename: data.rulename } })}
  </ModalHeader>
  <ModalBody>
    <div class="merge-view">
      <div class="actions">
        <label for="collapseUnchanged">
          <input id="collapseUnchanged" type="checkbox" bind:checked={collapseUnchanged} />
          {$format('label.COLLAPSE_UNCHANGED')}
        </label>
        {#if revertControls}
          <Button on:click={onRevertChanges} size="sm">{$format('label.UNDO_ALL')}</Button>
        {/if}
      </div>
      <div class="viewer">
        <MergeView
          bind:original={originalSource}
          bind:modified={sourceCopy}
          {onModifiedChange}
          bind:collapseUnchanged
          bind:revertControls
        />
      </div>
    </div>
  </ModalBody>
  <ModalFooter>
    <div class="review-actions">
      {#if revertControls}
        <Button on:click={onReviewCancel} color="link">{$format('label.CANCEL')}</Button>
        <Button on:click={onReviewApply} color="primary">{$format('label.APPLY_CHANGES')}</Button>
      {:else}
        <Button on:click={onReviewClose} color="secondary">{$format('label.DONE_REVIEWING')}</Button>
        <Button color="primary" on:click={enableChoosingMode}>{$format('label.EDIT_CHANGES')}</Button>
      {/if}
    </div>
  </ModalFooter>
</Modal>

<svelte:window on:keydown={checkForTopPanel} on:click={checkForTopPanel} />

<style lang="scss" src="./signature-editor.scss"></style>
